/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import moment, { Moment } from 'moment'

/**
 * Convert UTC timestamp to a local moment preserving the UTC date.
 * Extracts UTC year/month/date and creates a LOCAL moment so that
 * .toDate() followed by local-time formatting always shows the
 * correct calendar date (matches legacy Utils.convertTimestampToUTCMoment).
 * @param timestamp - Unix timestamp in milliseconds
 */
export function convertTimestampToUTCMoment(timestamp: number): Moment {
  const utcMoment = moment.utc(timestamp)
  // Build a local moment from UTC date parts to avoid timezone day-shift
  return moment({
    year: utcMoment.year(),
    month: utcMoment.month(),
    date: utcMoment.date()
  })
}

/**
 * Convert local date to UTC timestamp
 * @param date - Date object or moment
 */
export function convertToUTCTimestamp(date: Date | Moment): number {
  if (moment.isMoment(date)) {
    return date.utc().valueOf()
  }
  return moment(date).utc().valueOf()
}

/**
 * Format date for API (YYYYMMDD format)
 * @param date - Date to format
 */
export function formatDateForAPI(date: Date | Moment | null): string | null {
  if (!date) return null
  if (moment.isMoment(date)) {
    return date.format('YYYYMMDD')
  }
  return moment(date).format('YYYYMMDD')
}

/**
 * Parse date from API format (YYYYMMDD)
 * @param dateString - Date string in YYYYMMDD format
 */
export function parseDateFromAPI(dateString: string): Moment | null {
  if (!dateString) return null
  return moment(dateString, 'YYYYMMDD')
}
